import {
    Thumbnail,
    Text,
    TextField,
    Box,
    IndexTable,
    useSetIndexFiltersMode,
    useIndexResourceState,
    useBreakpoints,
    Pagination,
    Card,
    IndexFilters,
    InlineStack,
    Checkbox,
    SkeletonThumbnail,
    RangeSlider,
    InlineError,
    EmptySearchResult,
    Badge,
    BlockStack,
} from '@shopify/polaris';
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PROGRAM_CONSTANTS } from '#client/pages/Programs/constants/programsConstants';
import { SHARED_CONSTANTS } from '#shared/sharedConstants';
import { useDebouncedCallback } from '#client/utils/helpers';
import { useProductTable, useProgramActions } from '#client/stores/program/useProgramStore';
import { useProductFilterStore, useProductFilterActions } from '#client/stores/productFilter/useProductFilterStore';
import { multiProductFilterService } from '#client/services/multiProductFilter/multiProductFilterServices';
import { useToast } from '#client/components/common/Toast/ToastContext';
import VariantsModal from '#client/pages/Programs/Program/components/VariantsModal';
import { useVariantsModal } from './useVariantsModal';
import { Link, useLocation } from 'react-router';

export default function ProductTable({ programId }) {
    const { open, productId: modalProductId, openModal, closeModal } = useVariantsModal();
    const { pathname } = useLocation();

    // Add refs to track initialization
    const priceRangeInitialized = useRef(false);
    const initialMinPrice = useRef(0);
    const initialMaxPrice = useRef(PROGRAM_CONSTANTS.MIN_HIGHEST_PRODUCT_PRICE);

    // Zustand store hooks
    const { mode, products = [], sortSelected, currentPage, allProducts, isLoadingMore, errors = {}, isProductsLoading, pageInfo } = useProductTable();
    const { setSortSelected, handlePageChange, updateProductOverride, applyOverridesToProducts, initializeProgramProducts } = useProgramActions();
    const snapshotId = useProductFilterStore.use.snapshotId();
    // Grouped programs show which rule groups each product qualifies through
    const isGrouped = useProductFilterStore.use.conditionType() === 'groupedRules';
    const totalProductCount = useProductFilterStore.use.totalProductCount();
    const filterCurrentPage = useProductFilterStore.use.currentPage();
    const { setAllProducts, setCurrentPage: setFilterCurrentPage, setTotalProductCount } = useProductFilterActions();

    // Keep these as local state - they're purely UI-related
    const [selected, setSelected] = useState(0);

    // Filter state
    const [productNameFilter, setProductNameFilter] = useState('');
    const [queryValueFilter, setQueryValueFilter] = useState('');
    const [skuFilter, setSkuFilter] = useState('');
    const [priceRangeFilter, setPriceRangeFilter] = useState([0, PROGRAM_CONSTANTS.MIN_HIGHEST_PRODUCT_PRICE]);

    // Separate states for selection and override
    const { selectedResources, allResourcesSelected, handleSelectionChange } = useIndexResourceState(products);

    const sortOptions = PROGRAM_CONSTANTS.SORT_OPTIONS;
    const itemsPerPage = SHARED_CONSTANTS.MAX_PRODUCTS_PER_SHOPIFY_CALL;

    const { addToast } = useToast();
    const { mode: filterMode, setMode } = useSetIndexFiltersMode('FILTERING');

    const getProgramMode = useCallback(() => {
        if (pathname.includes('/create')) return 'create';
        if (pathname.includes('/edit')) return 'edit';
        return 'view';
    }, [pathname]);

    const routeMode = useMemo(() => getProgramMode(), [getProgramMode]);
    const snapshotEnabled = !!snapshotId && routeMode !== 'view';

    // Local effective price helper w/o overridePrices
    const minEffectivePrice = useCallback((p) => {
        const base = p.minVariantPrice || 0;
        return p.priceOverridden ? Number(p.programPrice || 0) : Number(base);
    }, []);
    const maxEffectivePrice = useCallback((p) => {
        const base = p.minVariantPrice || 0;
        return p.priceOverridden ? Number(p.programPrice ?? 0) : Number(base);
    }, []);

    // Calculate price range from all products for RangeSlider
    const { minProductPrice, maxProductPrice } = useMemo(() => {
        const minProductPrices = allProducts.map((p) => minEffectivePrice(p));
        const maxProductPrices = allProducts.map((p) => maxEffectivePrice(p));
        const min = minProductPrices.length > 0 ? Math.min(...minProductPrices) : 0;
        const max = maxProductPrices.length > 0 ? Math.max(...maxProductPrices) : PROGRAM_CONSTANTS.MIN_HIGHEST_PRODUCT_PRICE;
        return { minProductPrice: min, maxProductPrice: max };
    }, [minEffectivePrice, maxEffectivePrice, allProducts]);

    // Initialize price range when products first load (without triggering filter logic)
    useEffect(() => {
        if (allProducts.length > 0 && !priceRangeInitialized.current) {
            // Store initial values
            initialMinPrice.current = minProductPrice;
            initialMaxPrice.current = maxProductPrice;

            // Set the filter to match the full range
            setPriceRangeFilter([minProductPrice, maxProductPrice]);
            priceRangeInitialized.current = true;
        }
    }, [allProducts.length, minProductPrice, maxProductPrice]);

    // Reset initialization flag when programId changes (new program loaded)
    useEffect(() => {
        priceRangeInitialized.current = false;
    }, [programId]);

    const handleProductNameChange = useCallback((value) => setProductNameFilter(value), [setProductNameFilter]);
    const handleQueryValueChange = useCallback((value) => setQueryValueFilter(value), [setQueryValueFilter]);
    const handleProductNameRemove = useCallback(() => setProductNameFilter(''), [setProductNameFilter]);
    const handleQueryValueRemove = useCallback(() => setQueryValueFilter(''), [setQueryValueFilter]);
    const handleSKUChange = useCallback((value) => setSkuFilter(value), [setSkuFilter]);
    const handleSKURemove = useCallback(() => setSkuFilter(''), [setSkuFilter]);

    const handleFiltersClearAll = () => {
        setProductNameFilter('');
        setQueryValueFilter('');
        setSkuFilter('');
        setPriceRangeFilter([initialMinPrice.current, initialMaxPrice.current]);
    };

    const debouncedSetPriceRange = useDebouncedCallback(
        (value) => {
            setPriceRangeFilter(value);
        },
        PROGRAM_CONSTANTS.FILTER_DEBOUNCE_DELAY,
        []
    );

    const handlePriceRangeChange = useCallback(
        (value) => {
            debouncedSetPriceRange(value);
        },
        [debouncedSetPriceRange]
    );

    const handlePriceRangeRemove = useCallback(() => {
        setPriceRangeFilter([initialMinPrice.current, initialMaxPrice.current]);
    }, []);

    // Check if price filter is actually filtering (not just at default range)
    const isPriceFilterActive = useMemo(() => {
        if (!priceRangeInitialized.current) return false;

        return priceRangeFilter[0] !== initialMinPrice.current || priceRangeFilter[1] !== initialMaxPrice.current;
    }, [priceRangeFilter]);

    // Check if any filters are active (for server-side search)
    const hasActiveFilters = useMemo(() => {
        return productNameFilter !== '' || skuFilter !== '' || isPriceFilterActive || queryValueFilter !== '';
    }, [productNameFilter, skuFilter, isPriceFilterActive, queryValueFilter]);

    // Check if custom sorting is applied (non-default)
    const hasCustomSorting = useMemo(() => {
        return sortSelected && sortSelected.length > 0 && sortSelected[0] !== 'product asc';
    }, [sortSelected]);

    // Build search params from active filters and sorting
    const buildSearchParams = useCallback(
        (page = currentPage) => {
            const params = {
                page,
            };

            // If no filters and default sorting, return null to use default endpoint
            if (!hasActiveFilters && !hasCustomSorting) {
                return null;
            }

            // Add sorting (always include if custom sort is selected)
            if (sortSelected && sortSelected.length > 0) {
                const [field, direction] = sortSelected[0].split(' ');
                params.sortBy = field === 'product' ? 'productName' : field;
                params.sortDirection = direction;
            }

            // Combine productName and queryValue into search param
            const searchTerms = [productNameFilter, skuFilter, queryValueFilter].filter(Boolean).join(' ');
            if (searchTerms) {
                params.search = searchTerms;
            }

            if (isPriceFilterActive) {
                params.minPrice = priceRangeFilter[0];
                params.maxPrice = priceRangeFilter[1];
            }

            return params;
        },
        [hasActiveFilters, hasCustomSorting, currentPage, productNameFilter, skuFilter, queryValueFilter, isPriceFilterActive, priceRangeFilter, sortSelected]
    );

    const {
        data: snapshotResponse,
        isError: isSnapshotError,
        error: snapshotError,
    } = useQuery({
        queryKey: ['snapshot-products', snapshotId, filterCurrentPage, productNameFilter, skuFilter, queryValueFilter, priceRangeFilter, sortSelected, hasActiveFilters, hasCustomSorting],
        queryFn: async () => {
            if (!snapshotId) return null;

            if (hasActiveFilters || hasCustomSorting) {
                const searchParams = buildSearchParams(filterCurrentPage);
                return multiProductFilterService.searchSnapshotProducts(snapshotId, {
                    page: searchParams?.page || filterCurrentPage,
                    limit: itemsPerPage,
                    search: searchParams?.search,
                    minPrice: searchParams?.minPrice,
                    maxPrice: searchParams?.maxPrice,
                    sortBy: searchParams?.sortBy,
                    sortDirection: searchParams?.sortDirection,
                });
            }

            return multiProductFilterService.getSnapshotPage(snapshotId, filterCurrentPage, itemsPerPage);
        },
        enabled: snapshotEnabled,
        placeholderData: (previousData) => previousData,
    });

    useEffect(() => {
        if (!snapshotResponse?.data) return;

        const results = snapshotResponse.data.variants || snapshotResponse.data.products;
        const paginationInfo = snapshotResponse.data.pagination;
        const snapshotInfo = snapshotResponse.data.snapshot;

        if (results) {
            const productsWithOverrides = applyOverridesToProducts(results);
            setAllProducts(productsWithOverrides);

            if (paginationInfo?.totalCount !== undefined) {
                setTotalProductCount(paginationInfo.totalCount);
            } else if (snapshotInfo?.resultCount !== undefined) {
                setTotalProductCount(snapshotInfo.resultCount);
            }
        }
    }, [snapshotResponse, applyOverridesToProducts, setAllProducts, setTotalProductCount]);

    useEffect(() => {
        if (!isSnapshotError) return;
        addToast({
            type: 'error',
            message: snapshotError?.response?.data?.error?.message || 'Failed to load products',
        });
    }, [isSnapshotError, snapshotError, addToast]);

    useEffect(() => {
        // Fetch products when programId/mode changes
        // Use snapshot if available (from MultiProductFilter search), otherwise fetch from DB
        if (!programId) return;

        // If we have a snapshot ID (from search), use that for pagination
        // Otherwise, fetch from the program products endpoint (edit mode or no filters)
        if (!snapshotId) {
            (async () => {
                await initializeProgramProducts(programId);
            })();
        }
    }, [programId, mode, snapshotId, initializeProgramProducts]);

    // Trigger server-side search/sort when filters or sorting changes
    useEffect(() => {
        if (!programId) return; // Skip if no programId

        // Snapshot mode - reset to page 1 only when filters/sorting change
        if (snapshotEnabled) {
            setFilterCurrentPage(1);
            return;
        }

        // Edit mode (no snapshot) - use program products endpoint
        if (hasActiveFilters || hasCustomSorting) {
            // Fetch with filters and/or sorting - always reset to page 1
            const searchParams = buildSearchParams(1);
            handlePageChange(1, searchParams);
        } else {
            // Reload initial products when filters are cleared and no custom sorting
            initializeProgramProducts(programId, mode);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasActiveFilters, hasCustomSorting, productNameFilter, skuFilter, isPriceFilterActive, priceRangeFilter, queryValueFilter, sortSelected, snapshotEnabled]);

    // Handle pagination via snapshot
    const handleSnapshotPageNavigation = useCallback(
        (newPage) => {
            if (!snapshotEnabled) return;
            setFilterCurrentPage(newPage);
        },
        [snapshotEnabled, setFilterCurrentPage]
    );

    // Toggle product override
    const handleOverrideChange = useCallback(
        (product, checked) => {
            if (mode === 'view' || !product) return;
            const minPrice = product.minVariantPrice != null ? product.minVariantPrice : 0;
            const fallback = minPrice ?? 0;
            updateProductOverride({
                productId: product.id,
                priceOverridden: checked,
                programPrice: fallback,
            });
        },
        [mode, updateProductOverride]
    );

    // Change product override price (also ensures override is on)
    const handlePriceChange = useCallback(
        (productId, value) => {
            if (mode === 'view') return;
            if (value === '' || PROGRAM_CONSTANTS.DECIMAL_REGEX.test(value)) {
                updateProductOverride({
                    productId,
                    priceOverridden: true,
                    programPrice: value,
                });
            }
        },
        [mode, updateProductOverride]
    );

    const filters = useMemo(
        () => [
            {
                key: 'productName',
                label: 'Product Name',
                filter: <TextField label="Product Name" value={productNameFilter} onChange={handleProductNameChange} autoComplete="off" labelHidden />,
                shortcut: true,
            },
            {
                key: 'SKU',
                label: 'SKU',
                filter: <TextField label="SKU" value={skuFilter} onChange={handleSKUChange} autoComplete="off" labelHidden />,
                shortcut: true,
            },
            {
                key: 'priceRange',
                label: 'Price Range',
                filter: <RangeSlider label="Price Range" value={priceRangeFilter} onChange={handlePriceRangeChange} min={initialMinPrice.current} max={initialMaxPrice.current} />,
                shortcut: true,
            },
        ],
        [productNameFilter, handleProductNameChange, skuFilter, handleSKUChange, priceRangeFilter, handlePriceRangeChange]
    );

    const appliedFilters = useMemo(() => {
        const filters = [];

        if (productNameFilter) {
            filters.push({
                key: 'productName',
                label: disambiguateLabel('productName', productNameFilter),
                onRemove: handleProductNameRemove,
            });
        }

        if (skuFilter) {
            filters.push({
                key: 'SKU',
                label: disambiguateLabel('SKU', skuFilter),
                onRemove: handleSKURemove,
            });
        }

        if (isPriceFilterActive) {
            filters.push({
                key: 'priceRange',
                label: `Price Range: $${priceRangeFilter[0].toFixed(2)} - $${priceRangeFilter[1].toFixed(2)}`,
                onRemove: handlePriceRangeRemove,
            });
        }

        return filters;
    }, [productNameFilter, handleProductNameRemove, skuFilter, handleSKURemove, isPriceFilterActive, priceRangeFilter, handlePriceRangeRemove]);

    const onHandleCancel = () => {
        handleFiltersClearAll();
    };

    const renderImage = (imageUrl, title) => {
        if (!imageUrl) {
            return <SkeletonThumbnail size="small" />;
        }
        return <Thumbnail source={imageUrl} alt={title || 'Product image'} size="small" />;
    };

    const handleExcludeChange = useCallback(
        (productId, checked) => {
            updateProductOverride({ productId, exclude: checked });
        },
        [updateProductOverride]
    );

    // Breakpoints for responsive design
    const breakpoints = useBreakpoints();

    const headings = [{ title: 'Product' }, { title: 'SKU' }, ...(isGrouped ? [{ title: 'Qualifies under' }] : []), { title: 'Override' }, { title: 'Price' }, { title: 'Exclude' }];

    const rowMarkup = allProducts.map((product, index) => {
        const { id, title, image, maxVariantPrice, minVariantPrice, variants } = product;

        if (!variants) return null;

        // Ensure numeric values for price formatting
        const minPrice = minVariantPrice != null ? Number(minVariantPrice) : null;
        const maxPrice = maxVariantPrice != null ? Number(maxVariantPrice) : null;
        const hasPriceRange = maxPrice != null && minPrice != null && maxPrice !== minPrice;
        const variantsCount = variants.length || 0;
        const isExcludeChecked = !!product.exclude;
        const isOverrideChecked = !!product.priceOverridden;
        let variantCountDisplay = variantsCount > 1 || variantsCount === 0 ? `${variantsCount} variants` : `${variantsCount} variant`;
        let priceDisplay = hasPriceRange ? `${(minPrice ?? 0).toFixed(2)} - ${(maxPrice ?? 0).toFixed(2)}` : `${(minPrice ?? 0).toFixed(2)}`;

        if (isOverrideChecked) {
            priceDisplay = `${product.programPrice}`;
        }
        if (isExcludeChecked) {
            variantCountDisplay = 'Excluded';
        }

        const skuOrVariantContent = (
            <Link to="#" onClick={() => openModal(programId, id)}>
                <Text
                    variant="bodyMd"
                    as="span"
                    color="subdued"
                    style={{
                        cursor: 'pointer',
                        textDecoration: 'underline',
                    }}
                >
                    {variantCountDisplay}
                </Text>
            </Link>
        );

        return (
            <IndexTable.Row id={id} key={id} selected={selectedResources.includes(id)} position={index}>
                <IndexTable.Cell>
                    <Box paddingBlockEnd="200">
                        <InlineStack gap="400" align="start" blockAlign="center">
                            {renderImage(image, title)}
                            <Box
                                style={{
                                    maxWidth: 300,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    display: 'inline-block',
                                    verticalAlign: 'middle',
                                }}
                            >
                                <Text variant="bodyMd" fontWeight="bold" as="span" title={title}>
                                    {title}
                                </Text>
                            </Box>
                        </InlineStack>
                    </Box>
                </IndexTable.Cell>
                <IndexTable.Cell>{skuOrVariantContent}</IndexTable.Cell>
                {isGrouped && (
                    <IndexTable.Cell>
                        <BlockStack gap="100" inlineAlign="start">
                            {(product.qualifiesUnder || []).map((group) => (
                                <Badge key={group.name} tone={group.isRequired ? 'info' : undefined}>
                                    {group.name}
                                </Badge>
                            ))}
                        </BlockStack>
                    </IndexTable.Cell>
                )}
                <IndexTable.Cell>
                    <Box paddingBlockEnd="200">
                        <InlineStack gap="400" align="start" blockAlign="center">
                            <Checkbox
                                labelHidden
                                label="Override price"
                                checked={isOverrideChecked}
                                onChange={(checked) => {
                                    handleOverrideChange(product, checked);
                                }}
                                disabled={mode === 'view' || isExcludeChecked}
                            />
                        </InlineStack>
                    </Box>
                </IndexTable.Cell>
                <IndexTable.Cell>
                    <Box paddingBlockEnd="200">
                        <TextField
                            label="Override price"
                            value={priceDisplay}
                            onChange={(value) => handlePriceChange(product.id, value)}
                            type="text"
                            inputMode="decimal"
                            autoComplete="off"
                            disabled={mode === 'view' || !isOverrideChecked}
                            labelHidden
                            prefix={'$'}
                            style={{ width: '100%' }}
                        />
                    </Box>
                </IndexTable.Cell>
                <IndexTable.Cell>
                    <Box paddingBlockEnd="200">
                        <Checkbox
                            labelHidden
                            label="Exclude"
                            checked={isExcludeChecked}
                            onChange={(checked) => handleExcludeChange(product.id, checked)}
                            disabled={mode === 'view' || isOverrideChecked}
                        />
                    </Box>
                </IndexTable.Cell>
            </IndexTable.Row>
        );
    });

    let emptyStateMarkup = <EmptySearchResult title="No products found" description="Try adjusting your filters or search criteria." withIllustration />;
    // Show loading state when either the store isLoading is true OR when we're actively fetching products
    if (isProductsLoading) {
        emptyStateMarkup = <EmptySearchResult title="Loading products..." description="Please wait while we load the product data." withIllustration />;
    }

    return (
        <>
            <Card>
                <Box paddingBlock="400">
                    <Box paddingBlock="400">
                        <Text variant="headingLg">Included Product List</Text>
                    </Box>

                    {(errors.products || errors['products.products']) && (
                        <Box paddingBlockStart="200" paddingBlockEnd="200">
                            <InlineError
                                message={
                                    Array.isArray(errors.products)
                                        ? errors.products[0]
                                        : Array.isArray(errors['products.products'])
                                          ? errors['products.products'][0]
                                          : errors.products || errors['products.products']
                                }
                                fieldID="products-error"
                            />
                        </Box>
                    )}

                    {((mode === 'view' && allProducts.length > 0) || ((hasActiveFilters || hasCustomSorting) && allProducts.length === 0)) && (
                        <IndexFilters
                            sortOptions={sortOptions}
                            sortSelected={sortSelected}
                            queryValue={queryValueFilter}
                            queryPlaceholder="Search Products"
                            onQueryChange={handleQueryValueChange}
                            onQueryClear={handleQueryValueRemove}
                            onSort={setSortSelected}
                            cancelAction={{
                                onAction: onHandleCancel,
                                disabled: false,
                                loading: false,
                            }}
                            tabs={[]}
                            selected={selected}
                            onSelect={setSelected}
                            filters={filters}
                            appliedFilters={appliedFilters}
                            onClearAll={handleFiltersClearAll}
                            mode={filterMode}
                            setMode={setMode}
                            loading={isProductsLoading}
                            autoFocusSearchField={false}
                        />
                    )}
                    <IndexTable
                        condensed={breakpoints.smDown}
                        resourceName={PROGRAM_CONSTANTS.RESOURCE_PRODUCT_NAME}
                        itemCount={allProducts.length}
                        selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
                        onSelectionChange={handleSelectionChange}
                        headings={headings}
                        selectable={false}
                        // Allow sorting by product, SKU (variant-aware), price, override flag, and exclude flag
                        sortable={headings.map(({ title }) => title !== 'Qualifies under')}
                        emptyState={emptyStateMarkup}
                    >
                        {rowMarkup}
                    </IndexTable>
                    {allProducts.length > 0 && snapshotId && totalProductCount > itemsPerPage && (
                        <Box padding="400">
                            <Pagination
                                hasPrevious={filterCurrentPage > 1}
                                hasNext={filterCurrentPage * itemsPerPage < totalProductCount}
                                onPrevious={() => handleSnapshotPageNavigation(filterCurrentPage - 1)}
                                onNext={() => handleSnapshotPageNavigation(filterCurrentPage + 1)}
                                label={`${(filterCurrentPage - 1) * itemsPerPage + 1} - ${Math.min(filterCurrentPage * itemsPerPage, totalProductCount)} of ${totalProductCount}`}
                            />
                        </Box>
                    )}
                    {allProducts.length > 0 && !snapshotId && pageInfo && (
                        <Box padding="400">
                            <Pagination
                                hasPrevious={pageInfo.hasPreviousPage}
                                hasNext={pageInfo.hasNextPage}
                                onPrevious={() => handlePageChange(currentPage - 1, buildSearchParams(currentPage - 1))}
                                onNext={() => handlePageChange(currentPage + 1, buildSearchParams(currentPage + 1))}
                                label={`${(currentPage - 1) * itemsPerPage + 1} - ${Math.min(currentPage * itemsPerPage, pageInfo.totalCount || 0)} of ${pageInfo.totalCount || 0}`}
                                disabled={isLoadingMore}
                            />
                        </Box>
                    )}
                </Box>
            </Card>
            <VariantsModal open={open} onClose={closeModal} programId={programId} productId={modalProductId} mode={mode} />
        </>
    );
}

function disambiguateLabel(key, value) {
    switch (key) {
        case 'productName':
            return `Product name contains ${value}`;
        case 'SKU':
            return `SKU contains ${value}`;
        default:
            return value;
    }
}
